import {
  fetchCalibrationDeviceSessions,
  fetchCalibrationDevices,
  fetchCalibrationRunDiagnostics,
  fetchCalibrationSession,
  fetchCalibrationStations,
} from '@/features/calibration/api/calibration';
import {
  CALIBRATION_KEY,
  CALIBRATION_PAGE_LIMIT,
  CALIBRATION_RUN_POLL_INTERVAL_MS,
} from '@/features/calibration/config/constants';
import {
  infiniteQueryOptions,
  queryOptions,
  useQuery,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';

export const calibrationKeys = {
  all: [CALIBRATION_KEY] as const,
  stations: () => [CALIBRATION_KEY, 'stations'] as const,
  devices: () => [CALIBRATION_KEY, 'devices'] as const,
  deviceList: (stationId?: string) => [...calibrationKeys.devices(), stationId ?? 'all'] as const,
  sessions: (deviceId: string) => [CALIBRATION_KEY, 'sessions', deviceId] as const,
  session: (id: string) => [CALIBRATION_KEY, 'session', id] as const,
  diagnostics: (runId: string) => [CALIBRATION_KEY, 'run-diagnostics', runId] as const,
};

export function calibrationStationsQuery() {
  return infiniteQueryOptions({
    queryKey: calibrationKeys.stations(),
    queryFn: ({ pageParam, signal }) =>
      fetchCalibrationStations({ cursor: pageParam, limit: CALIBRATION_PAGE_LIMIT }, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    throwOnError: (_error, query) => query.state.data === undefined,
  });
}
export function calibrationDevicesQuery(stationId?: string) {
  return infiniteQueryOptions({
    queryKey: calibrationKeys.deviceList(stationId),
    queryFn: ({ pageParam, signal }) =>
      fetchCalibrationDevices(
        { stationId, cursor: pageParam, limit: CALIBRATION_PAGE_LIMIT },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    throwOnError: (_error, query) => query.state.data === undefined,
  });
}
export function calibrationSessionsQuery(deviceId: string) {
  return infiniteQueryOptions({
    queryKey: calibrationKeys.sessions(deviceId),
    queryFn: ({ pageParam, signal }) =>
      fetchCalibrationDeviceSessions(
        deviceId,
        { cursor: pageParam, limit: CALIBRATION_PAGE_LIMIT },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    throwOnError: (_error, query) => query.state.data === undefined,
  });
}
export const calibrationSessionQuery = (id: string) =>
  queryOptions({
    queryKey: calibrationKeys.session(id),
    queryFn: ({ signal }) => fetchCalibrationSession(id, { signal }),
    retry: false,
    refetchInterval: (query) => {
      const detail = query.state.data;
      const hasPendingPair = detail?.runPairs.some(
        ({ status }) => status === 'queued' || status === 'running',
      );
      const hasRegisteredLegacyRun = detail?.runs.some(({ run }) => run.status === 'registered');

      return hasPendingPair || hasRegisteredLegacyRun ? CALIBRATION_RUN_POLL_INTERVAL_MS : false;
    },
  });
export const calibrationRunDiagnosticsQuery = (runId: string) =>
  queryOptions({
    queryKey: calibrationKeys.diagnostics(runId),
    queryFn: ({ signal }) => fetchCalibrationRunDiagnostics(runId, { signal }),
    retry: false,
  });
export const useCalibrationStations = () => useSuspenseInfiniteQuery(calibrationStationsQuery());
export const useCalibrationDevices = (stationId?: string) =>
  useSuspenseInfiniteQuery(calibrationDevicesQuery(stationId));
export const useCalibrationSessions = (deviceId: string) =>
  useSuspenseInfiniteQuery(calibrationSessionsQuery(deviceId));
export const useCalibrationSession = (id: string) => useSuspenseQuery(calibrationSessionQuery(id));
export const useCalibrationRunDiagnostics = (runId: string, enabled: boolean) =>
  useQuery({ ...calibrationRunDiagnosticsQuery(runId), enabled });
